import { Menu, X } from "lucide-react";
import { useCallback, useState } from "react";
import { Link } from "react-router";
import type { ClientSession } from "../../api/auth";
import { useHeaderScroll } from "../../hooks/useHeaderScroll";
import { siteNav } from "./site-nav-data";
import { SiteHeaderAccount } from "./SiteHeaderAccount";
import { SiteHeaderNav } from "./SiteHeaderNav";
import { SiteHeaderSearch } from "./SiteHeaderSearch";
import { SiteMobileMenu } from "./SiteMobileMenu";
import "./site-header.css";

type SiteHeaderProps = {
  session: ClientSession;
  loading: boolean;
  onLogout: () => void;
  /** overlay：叠在页首内容上；bar：在文档流中吸顶。两者都是居中悬浮的岛。 */
  variant?: "overlay" | "bar";
};

const MENU_ID = "site-mobile-menu";

/**
 * 站点顶栏：居中悬浮的“岛”。滚动后品牌文字收起只留图标、岛随之变窄，
 * 底边的青色细线显示整页阅读进度。
 */
export function SiteHeader({ session, loading, onLogout, variant = "overlay" }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const { ref, scrolled } = useHeaderScroll<HTMLElement>();
  const items = siteNav.filter((item) => !item.auth || Boolean(session.id));
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const logout = () => {
    onLogout();
    setMenuOpen(false);
  };

  const className = [
    "site-header",
    variant === "bar" ? "is-bar" : "is-overlay",
    scrolled ? "is-scrolled" : "",
    menuOpen ? "is-menu-open" : "",
    searching ? "is-searching" : "",
  ].filter(Boolean).join(" ");

  return (
    <header ref={ref} className={className}>
      <div className="site-island">
        <Link className="site-brand" to="/" aria-label="MoodShow 首页">
          <img src="/icon-192.png" alt="" width={30} height={30} />
          <span className="site-brand-name">MoodShow</span>
        </Link>
        <span className="site-island-divider" aria-hidden="true" />
        <SiteHeaderNav items={items} />
        <span className="site-island-divider is-desktop" aria-hidden="true" />
        <div className="site-header-actions">
          <SiteHeaderSearch onOpenChange={setSearching} />
          <SiteHeaderAccount session={session} loading={loading} onLogout={logout} />
          <button
            type="button"
            className="site-menu-toggle"
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            aria-controls={MENU_ID}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>{menuOpen ? "关闭" : "菜单"}</span>
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        <span className="site-island-progress" aria-hidden="true"><i /></span>
      </div>
      <SiteMobileMenu
        id={MENU_ID}
        open={menuOpen}
        items={items}
        session={session}
        loading={loading}
        onClose={closeMenu}
        onLogout={logout}
      />
    </header>
  );
}
